
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { apiClient } from '../../utils/api';
import { logoImages } from '../../utils/images';
import './Footer.css'; // 添加自定义CSS文件

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setSubmitStatus('error');
      setStatusMessage('Please enter your email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setSubmitStatus('error');
      setStatusMessage('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await apiClient.subscribeNewsletter({ email });
      setSubmitStatus('success');
      setStatusMessage('Thank you for subscribing!');
      setEmail('');
    } catch (error) {
      setSubmitStatus('error');
      setStatusMessage(error instanceof Error ? error.message : 'Subscription failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-gray-900 text-white mt-0">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        {/* Main Footer Content */}
        <div className="py-12 md:py-16">
          <div className="footer-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10">
            {/* Company Info - Takes 1.5 columns for better balance */}
            <div className="footer-column flex flex-col lg:col-span-1 pr-6">
              <Link to="/" className="cursor-pointer mb-8 block">
                <img 
                  src={logoImages.white} 
                  alt="Musheng Logo" 
                  className="h-8 w-auto brightness-0 invert"
                />
              </Link>
              
              <h4 className="text-sm font-normal mb-6 text-white">Musheng Apparel Trading Co., Limited (Hong Kong)</h4>
              <div className="space-y-3 text-sm text-gray-300">
                <p className="flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
                  </svg>
                  +1 3473040960
                </p>
                <p className="flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                  </svg>
                  zhuolunli@musheng-apparel.com
                </p>
                <p className="flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"/>
                  </svg>
                  NEW YORK, US
                </p>
              </div>
            </div>

            {/* Newsletter - Column 2 */}
            <div className="footer-column flex flex-col lg:px-6">
              <h4 className="text-sm font-semibold mb-6 text-white tracking-wide">STAY UPDATED</h4>
              <p className="text-sm text-gray-300 mb-6 leading-relaxed">
                Get the latest Musheng news and industry insights delivered to your inbox.
              </p>
              <form onSubmit={handleNewsletterSubmit} className="space-y-4">
                <input
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 text-sm border border-gray-600 bg-gray-800 text-white focus:outline-none focus:border-white focus:ring-1 focus:ring-white placeholder-gray-400 rounded transition-all disabled:opacity-50"
                  required
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 bg-white text-gray-900 text-sm font-semibold hover:bg-gray-100 transition-colors rounded cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'SUBSCRIBING...' : 'SUBSCRIBE NOW'}
                </button>
              </form>

              {submitStatus === 'success' && (
                <div className="text-center p-2 bg-green-800 bg-opacity-50 border border-green-600 text-green-200 text-xs rounded">
                  {statusMessage}
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="text-center p-2 bg-red-800 bg-opacity-50 border border-red-600 text-red-200 text-xs rounded">
                  {statusMessage}
                </div>
              )}

              {/* Social Media Links */}
              <div className="mt-8 pt-6 border-t border-gray-700">
                <p className="text-xs text-gray-400 mb-3">Follow us for updates</p>
                <div className="flex space-x-3">
                  {/* <a href="#" className="text-gray-400 hover:text-white transition-colors"> */}
                    {/* <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"> */}
                      {/* <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/> */}
                    {/* </svg> */}
                  {/* </a> */}
                  <a href="https://www.linkedin.com/company/musheng-apparel-trading-co-limited/" className="text-gray-400 hover:text-white transition-colors">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>

            {/* Services - Column 3 */}
            <div className="footer-column flex flex-col lg:px-4">
              <h4 className="text-sm font-semibold mb-6 text-white tracking-wide">SERVICES</h4>
              <ul className="space-y-3 text-sm">
                <li><Link to="/services" className="text-gray-300 hover:text-white transition-colors">MANUFACTURING</Link></li>
                <li><Link to="/services" className="text-gray-300 hover:text-white transition-colors">DESIGN & DEVELOPMENT</Link></li>
                <li><Link to="/services" className="text-gray-300 hover:text-white transition-colors">QUALITY CONTROL</Link></li>
                <li><Link to="/services" className="text-gray-300 hover:text-white transition-colors">SUPPLY CHAIN</Link></li>
                <li><Link to="/sustainability" className="text-gray-300 hover:text-white transition-colors">SUSTAINABILITY</Link></li>
              </ul>
            </div>

            {/* Quick Links - Column 4 */}
            <div className="footer-column flex flex-col lg:px-4">
              <h4 className="text-sm font-semibold mb-6 text-white tracking-wide">QUICK LINKS</h4>
              <ul className="space-y-3 text-sm">
                <li><Link to="/" className="text-gray-300 hover:text-white transition-colors">HOME</Link></li>
                <li><Link to="/about" className="text-gray-300 hover:text-white transition-colors">ABOUT US</Link></li>
                <li><Link to="/products" className="text-gray-300 hover:text-white transition-colors">PRODUCTS</Link></li>
                <li><Link to="/contact" className="text-gray-300 hover:text-white transition-colors">CONTACT US</Link></li>
              </ul>
            </div>

          </div>
        </div>

        {/* Copyright */}
        <div className="py-8 border-t border-gray-700 mt-8">
          <div className="flex flex-col sm:flex-row justify-between items-center text-sm text-gray-400 space-y-2 sm:space-y-0">
            <span>© 2024 Musheng Apparel Trading Co., Ltd. All rights reserved.</span>
            <div className="flex space-x-6">
              <a href="/privacy" className="hover:text-white transition-colors">PRIVACY POLICY</a>
              <a href="/terms" className="hover:text-white transition-colors">TERMS OF SERVICE</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
